import React, { useState, useMemo, useCallback, useEffect, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { secureDelete, secureGet, securePost } from "../../config/api";
import { formatDateTime, formatAmount } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import DeletePopup from "../../components/DeletePopup";
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from "../../components/Breadcrumbs";
import LoadingLogo from "../../components/LoadingLogo";
import UserContext from "../../Shared/UserContext";

import trashIcon from "../../icons/trashW.svg";
import checkIcon from "../../icons/check.svg";
import editIcon from "../../icons/editB.svg";
import rejectIcon from "../../icons/reject.svg";

const STATUS_COLORS = {
    VALIDATED: "#06D6A0",
    UNDER_APPROVAL: "#FEA26F",
    CANCELLED: "#EF466F",
};

const getStateStyle = (status) => ({
    backgroundColor: STATUS_COLORS[status] || "#38485C",
    color: "white",
    padding: "4px 8px",
    borderRadius: "999px",
    textAlign: "center",
    fontSize: "12px",
})

const ExpenseDetails = () => {
    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation();
    const { id } = useParams();
    const [expense, setExpense] = useState({});
    const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [loading, setLoading] = useState(false);
    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };
    const { state: { role } } = useContext(UserContext);

    const abortController = useMemo(() => new AbortController(), []);
    const { signal } = abortController;

    const fetchExpense = useCallback(async () => {
        setLoading(true);
        try {
            const response = await secureGet(`/expenses/${id}`, showSnackbar, { signal });
            const fetchedExpense = response.data;
            setExpense(fetchedExpense);
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error("Failed to fetch expense:", error);
            }
        } finally {
            setLoading(false);
        }
    }, [id, signal]);

    useEffect(() => {
        fetchExpense();

        return () => {
            abortController.abort();
        };
    }, [id, abortController, fetchExpense]);


    const getStatusLabel = (status) => t(`expense_status.${status}`) || "N/A";
    const handleDeleteClick = () => setDeletePopupVisible(true);

    const handleDeleteConfirm = async () => {
        setDeleting(true);
        try {
            await secureDelete(`/expenses/delete/${expense.id}`, showSnackbar);
            showSnackbar(t('confirmation.expenseDelete'), 'success');
            setDeletePopupVisible(false);
            history.push({
                pathname: "/expenses",
                state: {
                    showSnackbar: true,
                    snackbarMessage: t(`confirmation.expenseDelete`),
                    snackbarSeverity: 'success',
                    filters: location.state?.filters || {},
                    page: location.state?.page || 0,
                    size: location.state?.size || 30,
                },
            });
        } catch (error) {
            console.error("Error deleting expense:", error);
        } finally {
            setDeleting(false);
            setDeletePopupVisible(false);
        }
    };

    const handleRejectClick = async () => {
        const payload = {
            expenseId: expense.id,
            status: "DECLINED",
        }
        try {
            await securePost(`/expenses/updateStatus`, payload, showSnackbar);
            history.push({
                pathname: "/expenses",
                state: {
                    showSnackbar: true,
                    snackbarMessage: t(`confirmation.expenseRejection`),
                    snackbarSeverity: 'success'
                }
            });
        } catch (error) {
            console.error("Error rejecting expense:", error);
        }
    }

    const handleValidateClick = async () => {
        const payload = {
            expenseId: expense.id,
            status: "VALIDATED",
        }
        try {
            await securePost(`/expenses/updateStatus`, payload, showSnackbar);
            history.push({
                pathname: "/expenses",
                state: {
                    showSnackbar: true,
                    snackbarMessage: t(`confirmation.expenseValidation`),
                    snackbarSeverity: 'success'
                }
            });
        } catch (error) {
            console.error("Error rejecting expense:", error);
        }
    }

    const handleModify = () => {
        history.push({
            pathname: `/expenses/details/modify/${expense.id}`,
            state: { filters: location.state?.filters || {}, page: location.state?.page || 0, size: location.state?.size || 30 },
        });
    }

    const renderActionButtons = () => (
        <>
            {(expense.status === "UNDER_APPROVAL") && (
                <React.Fragment>
                    {role === "ROLE_ADMIN" && (
                        <React.Fragment>

                            <button
                                className="flex items-center justify-center bg-[#0AD69F] text-[#FFFFFF] font-bold text-[14px] px-6 py-3 rounded-[12px] gap-x-2"
                                onClick={handleValidateClick}
                            >
                                <img src={checkIcon} alt="" className="w-5 h-5" />
                                {t(`actions.validate`)}
                            </button>
                            <button
                                className="flex items-center justify-center bg-[#EF476F] text-[#FFFFFF] font-bold text-[14px] px-6 py-3 rounded-[12px] gap-x-2"
                                onClick={handleRejectClick}
                            >
                                <img src={rejectIcon} alt="" className="w-5 h-5" />
                                {t(`actions.reject`)}
                            </button>
                        </React.Fragment>
                    )}
                    <button
                        className="flex items-center justify-center bg-white text-[#043F88] px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 "
                        onClick={handleModify}>
                        <img src={editIcon} alt="" className="w-5 h-5" />
                        {t(`actions.modify`)}
                    </button>
                </React.Fragment>
            )}
            {(expense.status !== "CANCELLED") && (
                <button
                    className="flex items-center justify-center bg-[#EF476F] text-white px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2"
                    onClick={handleDeleteClick}
                >
                    <img src={trashIcon} alt="delete" className="w-5 h-5" />
                    {t('actions.delete')}
                </button>
            )}
        </>
    );

    if (loading) return <LoadingLogo />;

    const ExpenseState = {
        label: getStatusLabel(expense.status).toUpperCase(),
        style: getStateStyle(expense.status),
    };



    return (
        <div className="">
            <Helmet>
                <title>GrayFox - {t("titles.expense_details")}</title>
            </Helmet>

            <div className="mb-4">
                <Breadcrumbs />

                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-[40px] font-semibold text-black">
                            {t('titles.expense_details')}
                        </h1>
                        <span
                            className="inline-block py-1 rounded-full text-sm font-semibold text-white"
                            style={ExpenseState.style}
                        >
                            {ExpenseState.label}
                        </span>
                    </div>
                    <div className="flex space-x-4">{renderActionButtons()}</div>
                </div>
            </div>

            <div className="w-full flex justify-between bg-white py-4 pl-[32px] rounded-[12px] shadow-[0px_6px_26px_#626B7D12] mb-4">

                <div className="flex-1">
                    <p className="text-[#707070] text-[14px] font-semibold mb-1">
                        {t('rows.reference')}
                    </p>
                    <p className="text-[#38485C] text-[20px] font-semibold">
                        {expense.expenseNumber}
                    </p>
                </div>

                <div className="flex-1">
                    <p className="text-[#707070] text-[14px] font-semibold mb-1">
                        {t('rows.creationDate')}
                    </p>
                    <p className="text-[#38485C] text-[20px] font-semibold">
                        {formatDateTime(expense.createdAt)}
                    </p>
                </div>

                <div className="flex-1">
                    <p className="text-[#707070] text-[14px] font-semibold mb-1">
                        {t('rows.commercial')}
                    </p>
                    <p className="text-[#38485C] text-[20px] font-semibold">
                        {expense.user?.firstName} {expense.user?.lastName}
                    </p>
                </div>

                <div className="flex-1">
                    <p className="text-[#707070] text-[14px] font-semibold mb-1">
                        {t('rows.totalPrice')}
                    </p>
                    <p className="text-[#38485C] text-[20px] font-semibold">
                        {formatAmount(expense.price)}
                    </p>
                </div>

                <div className="flex-1">
                    <p className="text-[#707070] text-[14px] font-semibold mb-1">{t('rows.comment')}</p>
                    <p className="text-[#38485C] text-[20px] font-semibold">
                        {expense.comment || t('placeholder.no_comment')}
                    </p>
                </div>
            </div>

            <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-4">
                {t('fields_types.list')}
            </h3>

            <div className="space-y-4">
                {expense.details?.map((expense, index) => (
                    <div key={index}
                        className="grid grid-cols-[30%_30%_20%] items-center bg-white p-4 rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">

                        <div className="text-center">
                            <p className="text-[#707070] text-[12px] font-semibold">
                                {t('rows.category').toUpperCase()}
                            </p>
                            <p className="text-[#043F88] text-[20px] font-semibold">
                                {expense.category}
                            </p>
                        </div>

                        <div className="text-center">
                            <p className="text-[#707070] text-[12px] font-semibold">
                                {t('rows.details').toUpperCase()}
                            </p>
                            <p className="text-[#043F88] text-[20px] font-semibold">
                                {expense.comment || "---"}
                            </p>
                        </div>

                        <div className="text-center">
                            <p className="text-[#707070] text-[12px] font-semibold">
                                {t('rows.amount').toUpperCase()}
                            </p>
                            <p className="text-[#043F88] text-[20px] font-semibold">
                                {formatAmount(expense.price)}
                            </p>
                        </div>
                    </div>
                ))}
            </div>

            {isDeletePopupVisible && (
                <DeletePopup
                    open={isDeletePopupVisible}
                    onCancel={() => setDeletePopupVisible(false)}
                    onConfirm={handleDeleteConfirm}
                    loading={deleting}
                />
            )}

            <SnackbarNotification
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            />
        </div>
    );
};

export default ExpenseDetails;
